import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, null, {
        params: { email: email }
      });

      setSuccess(true);
    } catch (err) {
      setError('An error occurred. Please try again or contact support.');
      console.error('Password reset request error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>
              
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Check Your Email</h1>
              
              <p className="text-gray-600 mb-6">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
              
              <p className="text-sm text-gray-500 mb-6">
                The link will expire in 1 hour. If you don't receive the email within a few minutes, check your spam folder.
              </p>

              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Return to Login
              </Button>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Didn't receive the email? Try again
                </button>
              </div>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>

          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-2">
            Forgot Password?
          </h1>
          <p className="text-gray-600 text-center mb-8">
            No worries! Enter your email and we'll send you reset instructions.
          </p>

          <Card className="p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <div className="text-center pt-4">
                <p className="text-sm text-gray-600">
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    Log In
                  </button>
                </p>
              </div>
            </form>
          </Card>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Need help?{' '}
              <a href="mailto:info@trucknsendit.com" className="text-blue-600 hover:text-blue-700">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ForgotPassword;
